import { RegularButton } from "@components/button"
import { Modal } from "@components/modal"
import { FlexBox, LoadingBox } from "@components/wrapper"
import { HARISENIN_PUBLIC_ICON } from "@constants/const/pictures"
import { flex_center } from "@constants/styles"
import CourseDetailServices from "@lib/api/services/course-detail.services"
import clsx from "clsx"
import Image from "next/image"
import { FC, useEffect, useState } from "react"
import ReactDOM from "react-dom"
import { BsFillEnvelopeFill, BsShare } from "react-icons/bs"
import { FaFacebookF, FaLinkedinIn, FaTelegramPlane, FaTwitter, FaWhatsapp } from "react-icons/fa"
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share"

type PopUpState = "none" | "modal" | "bottom-bar"

interface ClassShareProps {
  screenWidth: number
  slug: string
}

interface ShareButtonProps {
  label: string
  children: any
}

const ShareButton: FC<ShareButtonProps> = ({ label, children }) => {
  return (
    <FlexBox className="sm:w-[16%] w-1/3" align="center" direction="col">
      {children}
      <p className="text-center text-sm sm:mb-0 mb-8">{label}</p>
    </FlexBox>
  )
}

interface BottomShareProps {
  open: boolean
  onClose: () => void
  children: any
}

const BottomShare: FC<BottomShareProps> = ({ open, onClose, children }) => {
  if (!open) {
    return <></>
  }

  return ReactDOM.createPortal(
    <div className="top-0 left-0 fixed w-full h-full z-[100]">
      {/*  Overlay for mobile view */}
      <div onClick={onClose} className="fixed w-full h-full bg-backdrop z-[100]" />
      {children}
    </div>,
    document.body
  )
}

export const CourseShare: FC<ClassShareProps> = ({ screenWidth, slug }) => {
  const [popUpState, setPopUpState] = useState<PopUpState>("none")
  const [isFetchingLink, setIsFetchingLink] = useState(false)
  const [shareLink, setShareLink] = useState("")
  const [linkCopied, setLinkCopied] = useState(false)

  const course = new CourseDetailServices()
  //
  useEffect(() => {
    const getLink = async () => {
      try {
        setIsFetchingLink(true)
        const res = await course.getShareLink(slug)

        if (res.isSuccess) {
          const data = res.getValue() as string
          setShareLink(data)
          setIsFetchingLink(false)
        } else {
          setShareLink(``)
          setIsFetchingLink(false)
        }

        setIsFetchingLink(false)
      } catch (e) {
        setShareLink(``)
        setIsFetchingLink(false)
      }
    }

    if (popUpState === "none") {
      setShareLink("")
    } else {
      getLink()
    }
  }, [popUpState])

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareLink ?? "")
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 3000)
  }

  const handleOpen = async () => {
    if (screenWidth > 768) {
      setPopUpState("modal")
    } else {
      setPopUpState("bottom-bar")
    }
  }

  const handleHideModal = () => {
    setPopUpState("none")
  }

  const EmailLink = () => {
    return (
      <ShareButton label="Email">
        <EmailShareButton
          url={shareLink}
          className={clsx(flex_center, "!bg-grey-69 wh-11 rounded-md mb-1.5")}
        >
          <BsFillEnvelopeFill className="text-2xl text-white" />
        </EmailShareButton>
      </ShareButton>
    )
  }

  const FaceBookLink = () => {
    return (
      <ShareButton label="Facebook">
        <FacebookShareButton
          url={shareLink}
          className={clsx(flex_center, "!bg-facebook wh-11 rounded-md mb-1.5")}
        >
          <FaFacebookF className="text-2xl text-white" />
        </FacebookShareButton>
      </ShareButton>
    )
  }

  const TwitterLink = () => {
    return (
      <ShareButton label="Twitter">
        <TwitterShareButton
          url={shareLink}
          className={clsx(flex_center, "!bg-twitter wh-11 rounded-md mb-1.5")}
        >
          <FaTwitter className="text-2xl text-white" />
        </TwitterShareButton>
      </ShareButton>
    )
  }

  const LinkedInLink = () => {
    return (
      <ShareButton label="LinkedIn">
        <LinkedinShareButton
          url={shareLink}
          className={clsx(flex_center, "!bg-linkedin wh-11 rounded-md mb-1.5")}
        >
          <FaLinkedinIn className="text-2xl text-white" />
        </LinkedinShareButton>
      </ShareButton>
    )
  }

  const WhatsappLink = () => {
    return (
      <ShareButton label="Whatsapp">
        <WhatsappShareButton
          url={shareLink}
          className={clsx(flex_center, "!bg-whatsapp wh-11 rounded-md mb-1.5")}
        >
          <FaWhatsapp className="text-[28px] text-white" />
        </WhatsappShareButton>
      </ShareButton>
    )
  }

  const TelegramLink = () => {
    return (
      <ShareButton label="Telegram">
        <TelegramShareButton
          url={shareLink}
          className={clsx(flex_center, "!bg-telegram wh-11 rounded-md mb-1.5")}
        >
          <FaTelegramPlane className="text-[26px] text-white" />
        </TelegramShareButton>
      </ShareButton>
    )
  }

  const LinkComponent = () => {
    return (
      <FlexBox
        className="bg-primary-grey relative border border-grey-ec py-3.5 px-[18px] mb-5 w-full"
        justify="between"
      >
        <button className="mr-2 flex items-center" onClick={handleCopy}>
          <Image
            className="text-green text-xl"
            src={`${HARISENIN_PUBLIC_ICON}/icon_copytext.png`}
            alt="copy_text"
            width={15}
            height={15}
          />
        </button>

        <div className="w-full text-ellipsis overflow-hidden whitespace-nowrap sm:text-base text-sm">
          {shareLink}
        </div>

        {linkCopied && (
          <small className="text-green absolute -bottom-[22px] left-2.5">
            Link berhasil disalin
          </small>
        )}
      </FlexBox>
    )
  }

  return (
    <>
      <RegularButton
        className={clsx(
          "flex items-center justify-center gap-3 font-semibold",
          "sm:w-[48px] sm:h-[48px] w-full",
          "sm-only:mb-5"
        )}
        onClick={handleOpen}
        variant={screenWidth > 576 ? "transparent" : "transparent-white"}
      >
        {screenWidth > 576 ? (
          <>
            <BsShare />
          </>
        ) : (
          "Bagikan Kelas"
        )}
      </RegularButton>

      <Modal
        show={popUpState === "modal"}
        onClose={handleHideModal}
        contentClassName="!p-[18px] sm:w-[35vw]"
      >
        {isFetchingLink ? (
          <LoadingBox />
        ) : (
          <>
            <div className="mb-9">
              <h4 className="text-lg font-semibold">Share Kelas Ini</h4>
            </div>

            <FlexBox justify="between" className="mb-4">
              <EmailLink />
              <FaceBookLink />
              <TwitterLink />
              <LinkedInLink />
              <WhatsappLink />
              <TelegramLink />
            </FlexBox>

            <LinkComponent />
          </>
        )}
      </Modal>

      <BottomShare open={popUpState === "bottom-bar"} onClose={handleHideModal}>
        <div
          className={clsx(
            "z-[200] w-full fixed bottom-0 left-0 bg-white rounded-t-[20px] transition-all",
            popUpState === "bottom-bar" ? "max-h-[50vh] py-2.5 px-5" : "max-h-0"
          )}
        >
          <h4 className=" font-semibold pt-5 pb-6">Share Kelas Ini</h4>

          <FlexBox justify="between" className="mb-4" wrap="wrap">
            <EmailLink />
            <FaceBookLink />
            <TwitterLink />
            <LinkedInLink />
            <WhatsappLink />
            <TelegramLink />
          </FlexBox>

          <LinkComponent />
        </div>
      </BottomShare>
    </>
  )
}
